import { eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useMemo } from 'react'
import { secondsToHours, sumNetWorkSeconds } from '@/lib/dates'
import { useStore } from '@/lib/pocketbase'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

interface UseApprovedTagEntriesOptions {
    tagId: string | undefined
    enabled?: boolean
}

export function useApprovedTagEntries({ tagId, enabled = true }: UseApprovedTagEntriesOptions) {
    const [locationsCollection, timeEntriesCollection] = useStore('locations', 'time_entries')

    const { data: allLocations = [], isLoading: isLoadingLocations } = useLiveQuery(
        q => (tagId && enabled ? q.from({ locations: locationsCollection }) : undefined),
        [tagId, enabled]
    )

    const locationsWithTag = useMemo(
        () => allLocations.filter(loc => loc.tags?.includes(tagId || '')),
        [allLocations, tagId]
    )

    const locationIds = useMemo(() => locationsWithTag.map(l => l.id), [locationsWithTag])

    const { isLoading: isLoadingEntries, data: allEntries = [] } = useLiveQuery(
        q =>
            tagId && enabled
                ? q
                      .from({ entries: timeEntriesCollection })
                      .where(({ entries }) => eq(entries.status, 'approved'))
                      .orderBy(({ entries }) => entries.clock_in, 'desc')
                : undefined,
        [tagId, enabled]
    )

    const entries = useMemo(
        () => allEntries.filter(entry => entry.location && locationIds.includes(entry.location)),
        [allEntries, locationIds]
    )

    const totalHours = secondsToHours(sumNetWorkSeconds(entries))

    return {
        entries: entries as TimeEntriesExpanded[],
        totalHours,
        isLoading: isLoadingLocations || isLoadingEntries,
        locationCount: locationsWithTag.length,
    }
}
